import { IImage } from '../../indigofair/data/IImage';
import { ITranslationOverride } from '../../indigofair/data/ITranslationOverride';
import { ProductVariationType } from '../../indigofair/data/ProductVariationType';

export namespace IProductVariationMap {
  export namespace IEntry {
    export enum VariationDisplayType {
      VARIATION_DISPLAY_TYPE_UNKNOWN = 'VARIATION_DISPLAY_TYPE_UNKNOWN',
      /**
       * one variation option available
       */
      SINGLE = 'SINGLE',
      /**
       * allOptionsHaveSwatches
       */
      SWATCH = 'SWATCH',
      /**
       * All options are less than or equal to 11 characters
       */
      SHORT = 'SHORT',
      /**
       * All options are longer than 11 characters and there are fewer than 6 options
       * Deprecated, defaults to DROPDOWN
       * @deprecated
       */
      LONG = 'LONG',
      /**
       * default
       */
      DROPDOWN = 'DROPDOWN',
    }

    export namespace IValue {
      /**
       * Return an instance of IValue with the provided fields. Any field not
       * listed will be populated with an empty value (an empty array,
       * or undefined, whichever is valid for the type of the value.)
       */
      export const build = (partial?: Partial<IValue>): IValue =>
        ({
          translation_overrides: [],
          ...(partial ?? {}),
        }) as IValue;
    }

    export interface IValue {
      value: string | undefined;
      image: IImage | undefined;
      /**
       * For the fieldName here, TranslationOverride.Field.DEFAULT_TEXT should be used
       */
      translation_overrides: Array<ITranslationOverride>;
    }

    /**
     * Return an instance of IEntry with the provided fields. Any field not
     * listed will be populated with an empty value (an empty array,
     * or undefined, whichever is valid for the type of the value.)
     */
    export const build = (partial?: Partial<IEntry>): IEntry =>
      ({
        values: [],
        entry_values: [],
        translation_overrides: [],
        ...(partial ?? {}),
      }) as IEntry;
  }

  export interface IEntry {
    name: string | undefined;
    /**
     * Read/Write still supported but entryValues should be used instead. Read/write support will eventually be removed.
     * For writes: May be empty (i.e use entryValues instead), identical to entryValues, or used instead of entryValues.
     *             If values and entry values are both set but differ, values will be taken to be the source of truth
     *             to support clients that are unaware of the entryValues field.
     * @deprecated
     */
    values: Array<string>;
    type: keyof typeof ProductVariationType | undefined;
    /**
     * Replaces values, see its comment above for valid usages
     */
    entry_values: Array<IProductVariationMap.IEntry.IValue>;
    /**
     * For the fieldName here, TranslationOverride.Field.DEFAULT_TEXT should be used
     */
    translation_overrides: Array<ITranslationOverride>;
    variation_display_type:
      | keyof typeof IProductVariationMap.IEntry.VariationDisplayType
      | undefined;
  }

  /**
   * Return an instance of IProductVariationMap with the provided fields. Any field not
   * listed will be populated with an empty value (an empty array,
   * or undefined, whichever is valid for the type of the value.)
   */
  export const build = (
    partial?: Partial<IProductVariationMap>
  ): IProductVariationMap =>
    ({
      entries: [],
      ...(partial ?? {}),
    }) as IProductVariationMap;
}

export interface IProductVariationMap {
  /**
   * The map entry keys (names) must be unique, but we cannot enforce this with protos
   */
  entries: Array<IProductVariationMap.IEntry>;
}
